import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import SeoHead from './SeoHead';
import SiteFooter from './SiteFooter';
import TrustBar from './TrustBar';
import { HOMEPAGE_BRANDS, SITE_URL } from '../data/seoLandingPages';
import { getBrandAccent, getBrandLogo } from '../utils/brandLogos';
import { getAvailableGiftCards } from '../firebase/giftcards';
import { formatPrice } from '../utils/formatPrice';

const homepageSteps = [
  {
    title: 'Kies je cadeaubon',
    text: 'Bekijk het aanbod en kies een cadeaukaart met korting.',
  },
  {
    title: 'Betaal veilig via iDEAL',
    text: 'Reken af met iDEAL. Je betaling blijft beschermd totdat je de bon hebt gecheckt.',
  },
  {
    title: 'Ontvang je code',
    text: 'Na verificatie ontvang je de code per e-mail. Klaar om te gebruiken in de winkel of online.',
  },
];

const getDiscountPercentage = (originalValue, sellingPrice) => {
  const original = Number(originalValue || 0);
  const price = Number(sellingPrice || 0);

  if (original <= 0 || Number.isNaN(original) || Number.isNaN(price)) {
    return 0;
  }

  return Math.max(0, Math.round(((original - price) / original) * 100));
};

const LandingPage = () => {
  const [activeBrands, setActiveBrands] = useState([]);
  const [activeListingCount, setActiveListingCount] = useState(0);

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        name: 'Swapify',
        url: SITE_URL,
        description: 'Koop cadeaubonnen met korting of verkoop je ongebruikte cadeaubon.',
      },
      {
        '@type': 'SiteNavigationElement',
        name: 'Cadeaubonnen kopen',
        url: `${SITE_URL}/cadeaubonnen-kopen`,
      },
      {
        '@type': 'SiteNavigationElement',
        name: 'Cadeaubonnen verkopen',
        url: `${SITE_URL}/cadeaubonnen-verkopen`,
      },
      {
        '@type': 'SiteNavigationElement',
        name: 'Zo werkt het',
        url: `${SITE_URL}/how-it-works`,
      },
      {
        '@type': 'SiteNavigationElement',
        name: 'Marktplaats',
        url: `${SITE_URL}/marketplace`,
      },
    ],
  };

  useEffect(() => {
    let isMounted = true;

    const loadActiveBrands = async () => {
      try {
        const cards = await getAvailableGiftCards();
        if (isMounted) {
          setActiveListingCount(cards.length);
        }
        const grouped = cards.reduce((accumulator, card) => {
          const sellingPrice = Number(card.sellingPrice);
          const originalValue = Number(card.originalValue || card.cardValue || card.sellingPrice);

          if (!card.brand || Number.isNaN(sellingPrice) || Number.isNaN(originalValue)) {
            return accumulator;
          }

          if (!accumulator[card.brand]) {
            accumulator[card.brand] = {
              brand: card.brand,
              count: 0,
              lowestPrice: sellingPrice,
              originalValue,
              discount: getDiscountPercentage(originalValue, sellingPrice),
            };
          }

          accumulator[card.brand].count += 1;
          if (sellingPrice < accumulator[card.brand].lowestPrice) {
            accumulator[card.brand].lowestPrice = sellingPrice;
            accumulator[card.brand].originalValue = originalValue;
            accumulator[card.brand].discount = getDiscountPercentage(originalValue, sellingPrice);
          }
          return accumulator;
        }, {});

        const nextBrands = Object.values(grouped)
          .sort((left, right) => {
            if (right.count !== left.count) {
              return right.count - left.count;
            }

            if (left.lowestPrice !== right.lowestPrice) {
              return left.lowestPrice - right.lowestPrice;
            }

            return left.brand.localeCompare(right.brand, 'nl');
          })
          .slice(0, 6);

        if (isMounted) {
          setActiveBrands(nextBrands);
        }
      } catch (error) {
        console.error('Error loading active listings for homepage:', error);
        if (isMounted) {
          setActiveListingCount(0);
          setActiveBrands([]);
        }
      }
    };

    loadActiveBrands();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(254,202,202,0.45),_transparent_35%),linear-gradient(180deg,_#fff7ed_0%,_#ffffff_34%,_#fff1f2_100%)]">
      <SeoHead
        title="Swapify - Cadeaubonnen kopen en verkopen met korting"
        description="Verkoop je cadeaubon eenvoudig via Swapify. Of koop er zelf een met korting."
        canonicalPath="/"
        structuredData={structuredData}
      />
      <Navbar />

      <section className="px-4 pb-16 pt-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-[2rem] bg-white/90 px-8 py-12 shadow-xl ring-1 ring-red-100 backdrop-blur-md md:px-14 md:py-16">
          <div className="max-w-3xl">
            <h1 className="mt-4 text-4xl font-bold tracking-tight text-gray-900 md:text-6xl">
              Leuk cadeau. Nog leuker op je rekening.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-gray-600">
              Verkoop je cadeaubon eenvoudig via Swapify. Of koop er zelf een met korting.
            </p>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/marketplace"
                className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 px-7 py-4 font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
              >
                Bekijk het aanbod
              </Link>
              <Link
                to="/sell"
                className="inline-flex items-center justify-center rounded-2xl border border-gray-300 px-7 py-4 font-semibold text-gray-700 transition hover:border-red-400 hover:text-red-500"
              >
                Verkoop je bon
              </Link>
            </div>
          </div>
        </div>
      </section>

      {activeListingCount >= 3 && activeBrands.length > 0 ? (
        <section className="px-4 py-8 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <h2 className="text-3xl font-bold text-gray-900">Nu beschikbaar met korting</h2>
            <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {activeBrands.map((brand) => (
                (() => {
                  const accent = getBrandAccent(brand.brand);
                  return (
                    <Link
                      key={brand.brand}
                      to={`/marketplace?brand=${encodeURIComponent(brand.brand)}`}
                      className="rounded-[1.75rem] bg-white p-6 shadow-sm ring-1 ring-gray-100 transition hover:-translate-y-0.5 hover:shadow-lg"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex h-12 items-center">
                          <img
                            src={getBrandLogo(brand.brand)}
                            alt={brand.brand}
                            width="80"
                            height="80"
                            className="h-10 w-auto object-contain"
                          />
                        </div>
                        <span className={`rounded-full border px-3 py-1 text-sm font-bold ${accent.backgroundClass} ${accent.textClass} ${accent.borderClass}`}>
                          -{brand.discount}%
                        </span>
                      </div>
                      <div className="mt-5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span className="text-base text-gray-400 line-through">{formatPrice(brand.originalValue)}</span>
                        <span className="text-xl font-semibold text-gray-900">vanaf {formatPrice(brand.lowestPrice)}</span>
                      </div>
                    </Link>
                  );
                })()
              ))}
            </div>
            <div className="mt-8">
              <Link to="/marketplace" className="text-sm font-semibold text-red-500 transition hover:text-red-600">
                Bekijk alle cadeaubonnen →
              </Link>
            </div>
          </div>
        </section>
      ) : null}

      <section className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-gray-900">Populaire merken</h2>
          <p className="mt-3 max-w-2xl text-gray-600">
            De beste deals op cadeaukaarten van winkels die je echt gebruikt.
          </p>
          <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {HOMEPAGE_BRANDS.map((brand) => (
              <Link
                key={brand.label}
                to={brand.to}
                className="rounded-[1.75rem] bg-white p-6 shadow-sm ring-1 ring-gray-100 transition hover:-translate-y-0.5 hover:shadow-lg"
              >
                <div className="flex h-12 items-center">
                  <img
                    src={getBrandLogo(brand.logoBrand || brand.label)}
                    alt={brand.label}
                    width="80"
                    height="80"
                    className="h-10 w-auto object-contain"
                  />
                </div>
                <p className="mt-5 min-h-[72px] text-lg font-semibold leading-7 text-gray-900">{brand.text}</p>
                <p className="mt-6 text-sm font-semibold text-red-500">{brand.cta}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-[2rem] bg-gray-900 p-8 text-white shadow-xl md:p-12">
          <h2 className="text-3xl font-bold">Zo werkt het</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {homepageSteps.map((step, index) => (
              <div key={step.title} className="rounded-[1.5rem] bg-white/8 p-6 ring-1 ring-white/10">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-lg font-bold text-gray-900">
                  {index + 1}
                </div>
                <h3 className="mt-5 text-xl font-semibold">{step.title}</h3>
                <p className="mt-3 text-sm leading-7 text-gray-200">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-8 sm:px-6 lg:px-8">
        <TrustBar />
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-[2rem] bg-gradient-to-r from-red-500 to-orange-500 p-10 text-white shadow-xl md:p-14">
          <h2 className="text-3xl font-bold md:text-4xl">Leuk, zo’n cadeaubon. Nog leuker als je er geld van maakt.</h2>
          <p className="mt-4 max-w-2xl text-base leading-7 text-red-50">
            Verkoop je ongebruikte cadeaubon eenvoudig via Swapify. Gratis aanmelden, uitbetaling na verkoop.
          </p>
          <Link
            to="/sell"
            className="mt-8 inline-flex items-center justify-center rounded-2xl bg-white px-7 py-4 font-semibold text-red-500 transition hover:-translate-y-0.5 hover:shadow-xl"
          >
            Verkoop je bon
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default LandingPage;
